import { CSSProperties, useEffect } from "react";
import ButtonSymphony from "elements/ButtonSymphony";
import useBrand from "Hooks/useBrand";
import useOnboardingV2, { OnboardingStep, ONBOARDING_VERSION_V3_GOALS } from "Hooks/useOnboardingV2";
import useScreen from "Hooks/useScreen";
import Lottie from "react-lottie";
import { useHistory, useLocation } from "react-router-dom";
import GetStartedLottie from 'assets/images/lotties/GetStarted.json';
import SocialConnectionsButtonsWithModals from "components/shareable/SocialConnectionsButtonsWithModals";
import Icon from "elements/Icon";
import SpotifyArtistSearch from "components/shareable/SpotifyArtistSearch";
import { ReactComponent as SymphonyLogo } from 'assets/images/logo.svg';
import CardSymphony from "elements/CardSymphony";
import { smartGoBack, styx } from "modules/Utils";
import PageSymphony from "elements/PageSymphony";
import SubscriptionDetails from "components/SubscriptionDetails";
import { PlanType } from "modules/Const";
import { BillingCycle } from "components/shareable/UpgradeToProModal/utils";
import GoalsModal from "components/shareable/GoalsModal";
import CreateNewCampaignModalV2 from "components/CreateNewCampaignModalV2";
import Sty from "modules/Sty";
import Colors from "modules/Colors";
import LoaderOverlay from "elements/LoaderOverlay";
import DebugBox from "components/shareable/DebugBox";
import { track } from "analytics";

const AddArtistV2 = () => {
    const history = useHistory()
    const location = useLocation()
    const {
        mobileView,
        mStyle,
        cssCalc
    } = useScreen()

    const {
        getAffiliatePartnerDetails,
        isPaidBrand,
        currentBrand,
    } = useBrand()

    const {
        setSelectedSpotifyArtist,
        setSelectedInstagramProfile,
        setSelectedTikTokProfile,

        selectedSpotifyArtist,
        selectedInstagramProfile,
        selectedTikTokProfile,

        onboardingStep,
        setOnboardingStep,

        createBrandOnboarding,
        isLoadingOnboarding,
        resetOnboarding,

        setSignupWithoutProfileName,

        selectedGoalUrl
    } = useOnboardingV2();

    const isGoals = location.pathname === '/goals'

    let defaultPlansToShow = [PlanType.LITE, PlanType.PRO]
    let defaultRecurringInterval = BillingCycle.MONTHLY

    const partnerDetails = getAffiliatePartnerDetails()

    let partnerDescriptionText: string = ''
    if (partnerDetails) {
        const partnerName = partnerDetails.partnerName
        partnerDescriptionText = `We're excited to support the ${partnerName} community. Setup your account to start growing your fanbase with Symphony.`
    }

    useEffect(() => {
        if (isGoals) {
            setOnboardingStep(OnboardingStep.GOALS)
        }
        return () => {
            resetOnboarding()
        }
    }, [])

    const trackGoBack = (step: OnboardingStep) => {
        track(`NEW ONBOARDING: Go Back from ${step}`, {
            version: ONBOARDING_VERSION_V3_GOALS,
        })
    }

    const handleGoBack = () => {
        switch (onboardingStep) {
            case OnboardingStep.ADD_ARTIST:
                trackGoBack(onboardingStep)
                smartGoBack(history, '/marketing')
                break
            case OnboardingStep.CREATE_CAMPAIGN:
                trackGoBack(onboardingStep)
                setOnboardingStep(OnboardingStep.GOALS)
                break
            case OnboardingStep.PAYWALL:
                trackGoBack(onboardingStep)
                if (selectedGoalUrl.includes('marketing') && !selectedGoalUrl.includes('presave')) {
                    setOnboardingStep(OnboardingStep.CREATE_CAMPAIGN)
                } else {
                    setOnboardingStep(OnboardingStep.GOALS)
                }
                break
            default:
                history.push('/marketing')
        }
    }

    const goToGoals = () => {
        // history.push('/goals')
        // setOnboardingStep(OnboardingStep.GOALS)
        history.push('/assistant')
    }

    const createBrandAndGoToGoals = async () => {
        await createBrandOnboarding()
        goToGoals()
    }

    const getAddArtistContentStyle = (): CSSProperties => {
        const defaultStyles = {
            flexDirection: 'column',
            alignItems: 'center',
            overflowY: 'scroll',
        }

        switch (onboardingStep) {
            case OnboardingStep.PAYWALL:
            case OnboardingStep.GOALS:
            case OnboardingStep.CREATE_CAMPAIGN:
                return {
                    ...defaultStyles,
                    paddingTop: mobileView ? 0 : 24,
                    paddingBottom: mobileView ? 0 : 24,
                    paddingLeft: mobileView ? 0 : 24,
                    paddingRight: mobileView ? 0 : 24,
                    backgroundColor: 'transparent',
                    background: 'linear-gradient(290.62deg, #8800FF 0.13%, #D7B3ED 43.95%, #A1F0D4 74.68%)',
                } as CSSProperties
            default:
                return {
                    backgroundColor: 'white',
                }
        }
    }

    const getIsContent = () => {
        switch (onboardingStep) {
            case OnboardingStep.PAYWALL:
            case OnboardingStep.GOALS:
            case OnboardingStep.CREATE_CAMPAIGN:
                return true
            default:
                return false
        }
    }

    const getIsCard = () => {
        switch (onboardingStep) {
            case OnboardingStep.PAYWALL:
            case OnboardingStep.GOALS:
            case OnboardingStep.CREATE_CAMPAIGN:
                return true
            default:
                return false
        }
    }

    const renderBackButton = () => {
        if (!currentBrand) {
            return null
        }

        return (
            <Icon
                className="backButtonFindProfile"
                style={styx(
                    sty.backButtonContainerGetStartedFindBrand,
                    {
                        marginBottom: onboardingStep === OnboardingStep.ADD_ARTIST ? 32 : 24,
                    }
                )}
                onClick={handleGoBack}
                icon="arrowLeft"
                isCircle
            />
        )
    }

    const renderLeftColumn = () => {
        return (
            <div
                className="leftColumnGetStartedFindBrand"
                style={styx(
                    mStyle(sty, 'leftColumnGetStartedFindBrand'),
                    { height: cssCalc() }
                )}
            >
                <div
                    className="leftColumnGetStartedFindBrand_inner"
                    style={sty.leftColumnGetStartedFindBrand_inner}
                >
                    <div className="logoContainerGetStartedFindBrand">
                        <SymphonyLogo height="30px" />
                    </div>
                    {onboardingStep === OnboardingStep.ADD_ARTIST &&
                        renderBackButton()
                    }
                    <CardSymphony
                        className="cardGetStartedFindBrand"
                        title="Let's setup your profile"
                        subtitle="Enter your artist name or Spotify URL."
                        isNoPadding
                    />
                    {partnerDescriptionText &&
                        <div className="partnerDescriptionTextGetStartedFindBrand">
                            {partnerDescriptionText}
                        </div>
                    }
                    <SpotifyArtistSearch
                        className="spotifyArtistSearchGetStartedFindBrand"
                        style={{
                            marginBottom: 32,
                        }}
                        styleArtistList={{
                            border: Sty.border,
                            borderRadius: 16,
                            padding: mobileView ? 8 : 16,
                            backgroundColor: Colors.greyLight,
                            boxShadow: Sty.boxShadowInset,
                        }}
                        heightArtistList={mobileView ? undefined : 500}
                        onClickCreateNewProfile={async (profileName: string) => {
                            setSignupWithoutProfileName(profileName)
                            await createBrandOnboarding()
                            goToGoals()
                        }}
                        artistLimit={25}
                        onSelectArtist={async (artist, existingBrand) => {
                            if (artist) {
                                setSelectedSpotifyArtist(artist)
                                await createBrandOnboarding()
                                goToGoals()
                            } else {
                                setSelectedSpotifyArtist(null)
                            }
                        }}
                    />

                    <SocialConnectionsButtonsWithModals
                        selectedTikTokProfile={selectedTikTokProfile}
                        selectedInstagramProfile={selectedInstagramProfile}
                        onTikTokSelect={async (profile) => {
                            setSelectedTikTokProfile(profile)
                            createBrandAndGoToGoals()
                        }}
                        onInstagramSelect={async (profile) => {
                            setSelectedInstagramProfile(profile)
                            createBrandAndGoToGoals()
                        }}
                        onSignupWithoutConnections={(profileName) => {
                            setSignupWithoutProfileName(profileName);
                            createBrandAndGoToGoals();
                        }}
                        isOnboarding
                    />
                </div>
            </div>
        )
    }

    const renderRightColumn = () => {
        return (
            <div
                className="rightColumnGetStartedFindBrand"
                style={styx(
                    mStyle(sty, 'rightColumnGetStartedFindBrand'),
                    { height: cssCalc() }
                )}
            >
                <Lottie
                    style={{ marginRight: -18 }}
                    options={{
                        loop: true,
                        autoplay: true,
                        animationData: GetStartedLottie,
                    }}
                />
            </div>
        )
    }

    const renderAddArtist = () => {
        return (
            <>
                {renderLeftColumn()}
                {renderRightColumn()}
            </>
        )
    }

    const renderPaywall = () => {
        if (partnerDetails?.shownPlansOnSignup) {
            defaultPlansToShow = partnerDetails.shownPlansOnSignup
        }
        if (partnerDetails?.defaultRecurringIntervalOnSignup) {
            defaultRecurringInterval = partnerDetails.defaultRecurringIntervalOnSignup
        }

        return (
            <>
                {onboardingStep === OnboardingStep.PAYWALL &&
                    renderBackButton()
                }
                <SubscriptionDetails
                    onlyShowPlans={defaultPlansToShow}
                    defaultRecurringInterval={defaultRecurringInterval}
                    successUrl={selectedGoalUrl}
                    cancelUrl={selectedGoalUrl}
                />
                <ButtonSymphony
                    className="subscriptionDetailsWelcomeSkipButton"
                    style={{
                        marginLeft: 'auto',
                        marginRight: 'auto',
                        marginTop: 8,
                    }}
                    onClick={() => {
                        track("NEW ONBOARDING: Skip Paywall", {
                            version: ONBOARDING_VERSION_V3_GOALS
                        })
                        history.push(selectedGoalUrl)
                    }}
                    variant="outlined"
                    size="large"
                >
                    Skip for now
                </ButtonSymphony>
            </>
        )
    }

    const renderGoal = () => {
        return (
            <>
                <CardSymphony
                    className="createSymphonyItemModalTitle"
                    style={{ marginBottom: 24 }}
                    title="What do you want to do on Symphony?"
                    isNoPadding
                />
                <GoalsModal
                    className="createSymphonyItemModalFromAddArtistV2"
                    isModal={false}
                />
            </>
        )
    }

    const renderCreateCampaign = () => {
        return (
            <>
                {onboardingStep === OnboardingStep.CREATE_CAMPAIGN &&
                    renderBackButton()
                }
                <CardSymphony
                    className="createSymphonyItemModalTitle"
                    title="What campaign do you want to run?"
                    isNoPadding
                />
                <CreateNewCampaignModalV2
                    className="createNewCampaignModalFromAddArtistV2"
                    isModal={false}
                    height={mobileView ? 345 : 298}
                    stylePlaybookContent={{
                        border: Sty.border
                    }}
                // isOnboarding
                // onSelectCampaign={() => {
                //     setOnboardingStep(OnboardingStep.PAYWALL)
                // }}
                />
            </>
        )
    }

    const renderAddArtistContent = () => {
        switch (onboardingStep) {
            case OnboardingStep.ADD_ARTIST:
                return renderAddArtist()
            case OnboardingStep.GOALS:
                return renderGoal()
            case OnboardingStep.CREATE_CAMPAIGN:
                return renderCreateCampaign()
            case OnboardingStep.PAYWALL:
                return renderPaywall()
            default:
                return null
        }
    }

    return (
        <PageSymphony
            className="addArtistV2Component"
            style={styx(
                sty.addArtistV2Component,
                getAddArtistContentStyle(),
                { height: cssCalc() }
            )}
            maxWidth={800}
            isNoPadding
            isContent={getIsContent()}
            isCard={getIsCard()}
        >
            {isLoadingOnboarding &&
                <LoaderOverlay
                    className="loaderOverlayFromAddArtistV2"
                    isTransparent
                    title="Creating your profile..."
                />
            }
            {renderAddArtistContent()}
        </PageSymphony>
    )
}

const sty: Record<string, CSSProperties> = {
    addArtistV2Component: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
    },
    leftColumnGetStartedFindBrand: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
        width: '50%',
        padding: 24,
        gap: 16,
        overflowY: 'scroll',
    },
    leftColumnGetStartedFindBrand_mobile: {
        width: '100%',
        padding: 16,
        justifyContent: 'flex-start',
        overflowY: 'auto',
    },
    backButtonContainerGetStartedFindBrand: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 24,
        cursor: 'pointer',
    },
    rightColumnGetStartedFindBrand: {
        width: '50%',
        background: Colors.gradientTeal,
        paddingLeft: 40,
        paddingTop: 24,
        paddingBottom: 24,
    },
    rightColumnGetStartedFindBrand_mobile: {
        display: 'none',
    },

    leftColumnGetStartedFindBrand_inner: {
        maxWidth: 680,
        width: '100%',
        marginLeft: 'auto',
        marginRight: 'auto',
    }
}

export default AddArtistV2;